import { useEffect, useMemo, useState } from 'react';
import { fetchSupplierOrderCargoContext } from '../orders/api/orderTransportApi';
import type { TransportSupplierCargoContextDto } from '../../types/transport';
import { supplierCargoContextFromDto, type SupplierCargoZdContext } from './supplierTruckCargo';

/**
 * Addendum A (rule 2026-10-01, contract round 8): the rule context of every
 * ZD on this truck — the server's `cargoContext`. A ZD saved on the truck
 * reads it from the truck detail (`savedContexts`, no request); a ZD new on
 * the truck (or a saved one without it on an older response) asks
 * GET /supplier-orders/:id/cargo-context once. The editor then resolves
 * „Na innych transportach” / „Pozostało” and the warnings live
 * (supplierTruckCargo.resolveSupplierCargoLines).
 *
 * Returns `contexts[zdId]`: `undefined` while loading, `null` when unknown
 * (failed read, no ZD valuation) — the editor then shows "nieznane", never 0.
 */
export function useSupplierCargoContext(
  supplierOrderIds: string[],
  {
    currentTransportId,
    enabled,
    savedContexts = {},
  }: {
    currentTransportId?: string | null;
    enabled: boolean;
    savedContexts?: Record<string, TransportSupplierCargoContextDto | null | undefined>;
  },
): Record<string, SupplierCargoZdContext | null | undefined> {
  const [fetched, setFetched] = useState<Record<string, SupplierCargoZdContext | null>>({});
  const toFetch = useMemo(
    () => supplierOrderIds.filter((id) => savedContexts[id] === undefined).sort().join(','),
    [supplierOrderIds, savedContexts],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const missing = toFetch.split(',').filter((id) => id && !(id in fetched));
    if (missing.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      const loaded = await Promise.all(missing.map(async (supplierOrderId) => {
        try {
          const dto = await fetchSupplierOrderCargoContext(supplierOrderId, currentTransportId);
          return [supplierOrderId, supplierCargoContextFromDto(dto)] as const;
        } catch (error) {
          return [supplierOrderId, null] as const;
        }
      }));
      if (cancelled) return;
      setFetched((previous) => ({ ...previous, ...Object.fromEntries(loaded) }));
    })();
    return () => {
      cancelled = true;
    };
    // `fetched` is read only to skip ZDs already loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toFetch, enabled, currentTransportId]);

  return useMemo(() => Object.fromEntries(supplierOrderIds.map((id) => [
    id,
    savedContexts[id] !== undefined ? supplierCargoContextFromDto(savedContexts[id]) : fetched[id],
  ])), [supplierOrderIds, savedContexts, fetched]);
}
